import React, { useEffect, useState } from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Button, CreatePasswordForm, ScreenAuth, Text, TextInput } from '@components'
import { ScreenAuthProps } from '@navigation'
import { useLanguage } from '@context'
import {
  clearJoinDraft,
  getCommonStyles,
  isValidEmail,
  loadJoinDraft,
  normalizeEmail,
  saveJoinDraft,
  translateApiError,
} from '@utils'
import { secureTokenStore } from '@store'
import {
  type EventSession,
  fetchAccountSecurity,
  useCompleteEventSignIn,
  useRequestEmailCode,
  useScreen,
  useSignInWithEmailCode,
} from '@hooks'

/**
 * Password-less sign-in: an emailed 6-digit code. For people who joined an
 * event with the short form (no password) — and for anyone who prefers it.
 */
const DRAFT_KEY = 'login-code'

interface LoginDraft {
  step: 'email' | 'code' | 'password'
  email: string
  code: string
  pendingSession: EventSession | null
}

export const LoginWithCode: React.FC<ScreenAuthProps<'LoginWithCode'>> = ({ navigation }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const { isMobile } = useScreen()
  const commonStyles = getCommonStyles(theme)
  // A reload lands back on the step (and the typed values) it left.
  const [draft] = useState(() => loadJoinDraft<LoginDraft>(DRAFT_KEY))
  const [email, setEmail] = useState(draft?.email ?? '')
  const [code, setCode] = useState(draft?.code ?? '')
  const [step, setStep] = useState<'email' | 'code' | 'password'>(draft?.step ?? 'email')
  const [pendingSession, setPendingSession] = useState<EventSession | null>(
    draft?.pendingSession ?? null,
  )
  useEffect(() => {
    if (step === 'email' && !email) clearJoinDraft(DRAFT_KEY)
    else saveJoinDraft(DRAFT_KEY, { step, email, code, pendingSession } satisfies LoginDraft)
  }, [step, email, code, pendingSession])
  const [error, setError] = useState('')
  const requestCode = useRequestEmailCode()
  const signIn = useSignInWithEmailCode()
  const completeSignIn = useCompleteEventSignIn()
  const busy = requestCode.isPending || signIn.isPending

  const send = () => {
    setError('')
    if (!isValidEmail(email)) {
      setError(t('events.join.invalidEmail'))
      return
    }
    requestCode.mutate(
      { email: normalizeEmail(email), purpose: 'sign-in' },
      {
        onSuccess: () => setStep('code'),
        onError: (err) => setError(translateApiError(t, err).message),
      },
    )
  }

  const submit = () => {
    setError('')
    signIn.mutate(
      { email: normalizeEmail(email), code },
      {
        onSuccess: async (session) => {
          if (!session.account.isOnboardingCompleted) {
            // Same as the password login: tokens first, then the onboarding.
            await secureTokenStore.setTokens(session.accessToken, session.refreshToken)
            navigation.navigate('RegistrationSteps')
            return
          }
          // No password yet (joined an event with a code)? Offer to create one
          // before entering the app; never block the login if the check fails.
          try {
            await secureTokenStore.setTokens(session.accessToken, session.refreshToken)
            const security = await fetchAccountSecurity()
            if (!security.hasPassword) {
              setPendingSession(session)
              setStep('password')
              return
            }
          } catch {
            // Optional step: fall through to a normal sign-in.
          }
          clearJoinDraft(DRAFT_KEY)
          await completeSignIn(session)
        },
        onError: (err) => setError(translateApiError(t, err).message),
      },
    )
  }

  const finishPasswordStep = () => {
    clearJoinDraft(DRAFT_KEY)
    if (pendingSession) void completeSignIn(pendingSession)
  }

  if (step === 'password') {
    return (
      <ScreenAuth title={t('createPassword.title')} isPending={false}>
        <View style={[styles.container, !isMobile && commonStyles.flexMaxAuth]}>
          <View style={styles.form}>
            <Text type="body" color={theme.colors.greyBlue}>
              {t('createPassword.subtitle')}
            </Text>
            <CreatePasswordForm onSaved={finishPasswordStep} onLater={finishPasswordStep} />
          </View>
        </View>
      </ScreenAuth>
    )
  }

  return (
    <ScreenAuth title={t('loginWithCode.title')} isPending={busy}>
      <View style={[styles.container, !isMobile && commonStyles.flexMaxAuth]}>
        <View style={styles.form}>
          <Text type="body" color={theme.colors.greyBlue}>
            {step === 'email'
              ? t('loginWithCode.subtitle')
              : t('events.join.codeSentTo', { email: normalizeEmail(email) })}
          </Text>
          {step === 'email' ? (
            <TextInput
              testID="login-code-email"
              placeholder={t('login.email')}
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
              iconRight="message"
              onSubmitEditing={send}
            />
          ) : (
            <TextInput
              testID="login-code-input"
              placeholder="000000"
              value={code}
              onChangeText={(value) => setCode(value.replace(/[^\d]/g, '').slice(0, 6))}
              keyboardType="number-pad"
              maxLength={6}
              onSubmitEditing={submit}
            />
          )}
          {error ? (
            <Text type="bodyExtraSmall" color={theme.colors.error}>
              {error}
            </Text>
          ) : null}
          {step === 'code' ? (
            <TouchableOpacity accessibilityRole="button" disabled={busy} onPress={send}>
              <Text type="bodySmallBold" color={theme.colors.primary}>
                {t('events.join.resend')}
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>
        <View style={styles.actions}>
          <Button
            title={step === 'email' ? t('events.join.sendCode') : t('login.button')}
            disabled={busy || (step === 'code' && code.length !== 6)}
            onPress={step === 'email' ? send : submit}
          />
          <TouchableOpacity accessibilityRole="button" onPress={() => navigation.navigate('Login')}>
            <Text type="bodyExtraSmall" color={theme.colors.black} style={styles.back}>
              {t('loginWithCode.withPassword')}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </ScreenAuth>
  )
}

const styles = StyleSheet.create({
  container: { flex: 1, width: '100%', justifyContent: 'space-between' },
  form: { paddingTop: 32, gap: 12 },
  actions: { alignItems: 'center', paddingBottom: 12 },
  back: { paddingTop: 16 },
})
